import {
  LOAN_DIFFERE_NONE,
  LOAN_PALIERS_MAX,
  LOAN_PALIERS_MIN,
  LOAN_PALIER_DIFFERE_CONTROL,
  LOAN_PALIER_DURATION_CONTROL,
  LOAN_PALIER_MONTHLY_PAYMENT_CONTROL,
  LoanDiffere,
  LoanPalier,
  LoanPalierFormValue,
  LoanPalierIssue,
  LoanPalierRange,
  LoanStep,
} from './loan-palier.model';

export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().replace(',', '.');

  if (normalized === '') {
    return null;
  }

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
}

export function toPositiveNumber(value: unknown): number | null {
  const parsed = toFiniteNumber(value);

  return parsed !== null && parsed > 0 ? parsed : null;
}

export function isLoanDiffere(value: unknown): value is LoanDiffere {
  return value === 'PARTIEL' || value === 'TOTAL';
}

function hasPalierContent(palier: LoanPalierFormValue): boolean {
  if (!palier) {
    return false;
  }

  return (
    toFiniteNumber(palier[LOAN_PALIER_DURATION_CONTROL]) !== null ||
    toFiniteNumber(palier[LOAN_PALIER_MONTHLY_PAYMENT_CONTROL]) !== null ||
    isLoanDiffere(palier[LOAN_PALIER_DIFFERE_CONTROL])
  );
}

export function paliersFormToApi(
  paliers: LoanPalierFormValue[] | null | undefined,
): LoanPalier[] | null {
  if (!Array.isArray(paliers)) {
    return null;
  }

  const filled = paliers.filter(hasPalierContent);

  if (!filled.length) {
    return null;
  }

  return filled.map((palier, index) => {
    const isLast = index === filled.length - 1;
    const differe = palier[LOAN_PALIER_DIFFERE_CONTROL];
    const duration = toFiniteNumber(palier[LOAN_PALIER_DURATION_CONTROL]);
    const monthlyPayment = toFiniteNumber(
      palier[LOAN_PALIER_MONTHLY_PAYMENT_CONTROL],
    );
    const mapped: LoanPalier = {};

    if (duration !== null) {
      mapped.durationInMonth = duration;
    }

    if (index === 0 && isLoanDiffere(differe)) {
      mapped.differe = differe;
    } else if (!isLast && monthlyPayment !== null) {
      mapped.monthlyPayment = monthlyPayment;
    }

    return mapped;
  });
}

export function paliersApiToForm(
  paliers: LoanPalier[] | null | undefined,
): LoanPalierFormValue[] {
  if (!Array.isArray(paliers) || !paliers.length) {
    return [];
  }

  return paliers.map(palier => ({
    durationInMonth: palier?.durationInMonth ?? '',
    monthlyPayment: palier?.monthlyPayment ?? '',
    differe: palier?.differe ?? LOAN_DIFFERE_NONE,
  }));
}

export function hasLoanPaliers(loan: unknown): boolean {
  const paliers = (loan as { paliers?: unknown })?.paliers;

  return Array.isArray(paliers) && paliers.length > 0;
}

export type LoanPalierLike = LoanPalierFormValue | LoanPalier;

export function sumPalierDurations(
  paliers: LoanPalierLike[] | null | undefined,
): number {
  if (!Array.isArray(paliers)) {
    return 0;
  }

  return paliers.reduce(
    (sum, palier) =>
      sum + (toFiniteNumber(palier?.[LOAN_PALIER_DURATION_CONTROL]) ?? 0),
    0,
  );
}

export function palierRanges(
  paliers: LoanPalierLike[] | null | undefined,
): LoanPalierRange[] {
  if (!Array.isArray(paliers)) {
    return [];
  }

  const ranges: LoanPalierRange[] = [];
  let startMonth = 1;

  paliers.forEach((palier, index) => {
    const duration =
      toFiniteNumber(palier?.[LOAN_PALIER_DURATION_CONTROL]) ?? 0;
    const differe = palier?.[LOAN_PALIER_DIFFERE_CONTROL];

    ranges.push({
      index,
      startMonth,
      endMonth: startMonth + Math.max(duration, 0) - 1,
      differe: index === 0 && isLoanDiffere(differe) ? differe : undefined,
    });

    startMonth += Math.max(duration, 0);
  });

  return ranges;
}

export function palierSegmentStartMonth(
  paliers: LoanPalierLike[] | null | undefined,
  palierIndex: number,
): number {
  if (!Array.isArray(paliers)) {
    return 1;
  }

  let startMonth = 1;

  for (let index = 0; index < palierIndex && index < paliers.length; index++) {
    startMonth +=
      toFiniteNumber(paliers[index]?.[LOAN_PALIER_DURATION_CONTROL]) ?? 0;
  }

  return startMonth;
}

export function palierBoundaryMonths(
  paliers: LoanPalierLike[] | null | undefined,
): Set<number> {
  const boundaries = new Set<number>();

  if (!Array.isArray(paliers) || paliers.length < LOAN_PALIERS_MIN) {
    return boundaries;
  }

  palierRanges(paliers)
    .slice(1)
    .forEach(range => boundaries.add(range.startMonth));

  return boundaries;
}

export function palierFirstMonthlyPayment(
  steps: LoanStep[] | null | undefined,
  startMonth: number,
): number | null {
  if (!Array.isArray(steps)) {
    return null;
  }

  const step = steps.find(item => item?.monthNumber === startMonth);

  if (!step) {
    return null;
  }

  return (step.loanMonth ?? 0) + (step.interestMonth ?? 0);
}

export function palierDurationIssueMessage(
  value: unknown,
  position: number,
): string | null {
  const duration = toPositiveNumber(value);

  if (duration === null) {
    return `Saisissez la durée du palier ${position} en mois`;
  }

  return Number.isInteger(duration)
    ? null
    : `Saisissez un nombre entier de mois pour le palier ${position}`;
}

export function palierMonthlyPaymentIssueMessage(position: number): string {
  return `Saisissez la mensualité hors assurance du palier ${position}`;
}

export function palierDiffereIssueMessage(position: number): string {
  return `Retirez le différé du palier ${position} : seul le premier palier peut en comporter un`;
}

export function paliersDurationSumIssueMessage(
  durationsSum: number,
  totalDuration: number,
): string {
  const gap = Math.abs(totalDuration - durationsSum);
  const correction =
    durationsSum > totalDuration
      ? `retirez ${gap} mois aux paliers`
      : `ajoutez ${gap} mois aux paliers`;

  return `Les paliers totalisent ${durationsSum} mois pour un crédit de ${totalDuration} mois : ${correction} ou ajustez la durée totale du crédit`;
}

export function validateLoanPaliers(
  paliers: LoanPalierFormValue[] | null | undefined,
  durationTotalInMonth: unknown,
): LoanPalierIssue[] {
  const list = Array.isArray(paliers) ? paliers : [];

  if (!list.length) {
    return [];
  }

  const issues: LoanPalierIssue[] = [];

  if (list.length < LOAN_PALIERS_MIN || list.length > LOAN_PALIERS_MAX) {
    issues.push({
      message: `Prévoyez entre ${LOAN_PALIERS_MIN} et ${LOAN_PALIERS_MAX} paliers`,
    });
  }

  let durationsSum = 0;
  let allDurationsValid = true;

  list.forEach((palier, index) => {
    const position = index + 1;
    const isLast = index === list.length - 1;
    const differe = isLoanDiffere(palier?.[LOAN_PALIER_DIFFERE_CONTROL]);
    const durationValue = palier?.[LOAN_PALIER_DURATION_CONTROL];
    const durationIssue = palierDurationIssueMessage(durationValue, position);

    if (durationIssue) {
      allDurationsValid = false;

      issues.push({
        message: durationIssue,
        palierIndex: index,
        field: LOAN_PALIER_DURATION_CONTROL,
      });
    } else {
      durationsSum += toPositiveNumber(durationValue) ?? 0;
    }

    if (differe && index !== 0) {
      issues.push({
        message: palierDiffereIssueMessage(position),
        palierIndex: index,
        field: LOAN_PALIER_DIFFERE_CONTROL,
      });
    }

    if (
      !isLast &&
      !differe &&
      toPositiveNumber(palier?.[LOAN_PALIER_MONTHLY_PAYMENT_CONTROL]) === null
    ) {
      issues.push({
        message: palierMonthlyPaymentIssueMessage(position),
        palierIndex: index,
        field: LOAN_PALIER_MONTHLY_PAYMENT_CONTROL,
      });
    }
  });

  const totalDuration = toFiniteNumber(durationTotalInMonth);

  if (
    allDurationsValid &&
    totalDuration !== null &&
    durationsSum !== totalDuration
  ) {
    issues.push({
      message: paliersDurationSumIssueMessage(durationsSum, totalDuration),
    });
  }

  return issues;
}

export function applyPaliersToLoanPayload<T>(payload: T, formValue: any): T {
  const target = payload as any;
  const paliers = paliersFormToApi(formValue?.paliers);
  const enabled = formValue?.paliersEnabled === true && paliers !== null;

  if (!enabled) {
    target.paliers = [];

    return payload;
  }

  target.paliers = paliers;
  target.amountMonthly = null;
  target.differe = undefined;
  target.durationDiffereInMonth = undefined;

  return payload;
}
